import { Routes, Route } from 'react-router-dom';
import { useAuthStore } from './stores/auth-store';
import { ProtectedRoute } from '@dak/ui';
import { Login } from './Login';
import { SignUp } from './SignUp';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { Dashboard } from './Dashboard';
import { useEffect } from 'react';
import { useRealtimeSync } from './hooks/useRealtimeSync';
import { useMembersStore } from './stores/members-store';
import { useChoresStore } from './stores/chores-store';
import { useInstancesStore } from './stores/instances-store';
import { usePointsStore } from './stores/points-store';
import { useSettingsStore } from './stores/settings-store';
import { useGoalsStore } from './stores/goals-store';

function AuthenticatedApp({ children }: { children: React.ReactNode }) {
  const { session } = useAuthStore();

  // Subscribe to realtime sync when logged in
  useRealtimeSync(session?.user?.id);

  // Fetch initial data when logged in
  useEffect(() => {
    if (session) {
      useMembersStore.getState().fetchMembers();
      useChoresStore.getState().fetchChores();
      usePointsStore.getState().fetchBalances();
      usePointsStore.getState().fetchPeriodPoints('week');
      useSettingsStore.getState().fetchSettings();

      // Ensure today's instances exist and fetch them
      // Also fetch goal progress after chores are loaded
      setTimeout(() => {
        useInstancesStore.getState().ensureTodayInstances();
        useGoalsStore.getState().fetchGoalProgress();
      }, 500);
    }
  }, [session]);

  return <>{children}</>;
}

export default function App() {
  const { initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Apply dark mode from query param (for iframe embedding)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dark') === 'true') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const { session, loading } = useAuthStore();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute session={session} loading={loading}>
            <AuthenticatedApp>
              <Dashboard />
            </AuthenticatedApp>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
